/**
 * Admin SEO Settings Page
 *
 * Configure site name, description, and organization schema.
 */

import type { FormEvent } from 'react';
import { useState, useEffect } from 'react';
import { useAdminApi } from '../../hooks/useAdminApi';
import type { SEOSettings, OrganizationSchema } from '@petmas/shared';
import { UI_CONSTANTS } from '@petmas/shared';
import { asSEOSettings, toSettingsRecord } from '../../utils/typeAssertions';

const defaultSettings: SEOSettings = {
  siteName: 'PetMas',
  siteDescription: 'Tienda de mascotas en Peru',
  defaultOgImage: '/og-image.jpg',
  organization: {
    name: 'PetMas',
    address: {
      street: '',
      city: '',
      region: '',
      postalCode: '',
      country: 'PE',
    },
    phone: [],
    email: '',
    hours: '',
    social: {},
  },
  schemaProductEnabled: true,
  schemaBreadcrumbEnabled: true,
  schemaOrganizationEnabled: true,
};

export function AdminSEOPage() {
  const { getSettings, updateSettings, isLoading, error: apiError } = useAdminApi();
  const [settings, setSettings] = useState<SEOSettings>(defaultSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await getSettings('seo');
      if (data) {
        setSettings({ ...defaultSettings, ...asSEOSettings(data) });
      }
    } catch {
      setError('Error al cargar configuracion');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      await updateSettings('seo', toSettingsRecord(settings));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), UI_CONSTANTS.TOAST_DURATION_MS);
    } catch {
      setError('Error al guardar configuracion');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof SEOSettings, value: unknown) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleOrgChange = (field: keyof OrganizationSchema, value: unknown) => {
    setSettings((prev) => ({
      ...prev,
      organization: { ...prev.organization, [field]: value },
    }));
  };

  const handleAddressChange = (field: keyof OrganizationSchema['address'], value: string) => {
    setSettings((prev) => ({
      ...prev,
      organization: {
        ...prev.organization,
        address: { ...prev.organization.address, [field]: value },
      },
    }));
  };

  const handleSocialChange = (platform: keyof OrganizationSchema['social'], value: string) => {
    setSettings((prev) => ({
      ...prev,
      organization: {
        ...prev.organization,
        social: { ...prev.organization.social, [platform]: value || undefined },
      },
    }));
  };

  const handlePhonesChange = (value: string) => {
    const phones = value.split(',').map((p) => p.trim()).filter(Boolean);
    handleOrgChange('phone', phones);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Configuracion de SEO</h2>
        <p className="text-gray-600 mb-6">
          Optimiza tu sitio para motores de busqueda y redes sociales.
        </p>

        {(error ?? apiError) && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error ?? apiError}
          </div>
        )}

        {saveSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            Configuracion guardada exitosamente
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic SEO */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Informacion Basica</h3>
            <div className="grid gap-4 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Sitio
                </label>
                <input
                  type="text"
                  value={settings.siteName}
                  onChange={(e) => handleChange('siteName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripcion del Sitio
                </label>
                <textarea
                  value={settings.siteDescription}
                  onChange={(e) => handleChange('siteDescription', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Aparece en resultados de busqueda (max 160 caracteres)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Imagen OG por Defecto
                </label>
                <input
                  type="text"
                  value={settings.defaultOgImage}
                  onChange={(e) => handleChange('defaultOgImage', e.target.value)}
                  placeholder="/og-image.jpg"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Imagen que aparece al compartir en redes sociales
                </p>
              </div>
            </div>
          </div>

          {/* Organization Info */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Informacion de la Empresa</h3>
            <div className="grid gap-4 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre Legal
                </label>
                <input
                  type="text"
                  value={settings.organization.name}
                  onChange={(e) => handleOrgChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Structured Address */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Direccion
                </label>
                <input
                  type="text"
                  value={settings.organization.address.street}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                  placeholder="Calle / Av. / Pasaje"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={settings.organization.address.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    placeholder="Ciudad (ej: Miraflores)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                  <input
                    type="text"
                    value={settings.organization.address.region}
                    onChange={(e) => handleAddressChange('region', e.target.value)}
                    placeholder="Region (ej: Lima)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={settings.organization.address.postalCode}
                    onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                    placeholder="Codigo Postal"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                  <input
                    type="text"
                    value={settings.organization.address.country}
                    onChange={(e) => handleAddressChange('country', e.target.value)}
                    placeholder="Pais (ej: PE)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefonos (separados por coma)
                </label>
                <input
                  type="text"
                  value={settings.organization.phone.join(', ')}
                  onChange={(e) => handlePhonesChange(e.target.value)}
                  placeholder="+51999999999, +51988888888"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={settings.organization.email}
                  onChange={(e) => handleOrgChange('email', e.target.value)}
                  placeholder="info@petmas.pe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Horario de Atencion
                </label>
                <input
                  type="text"
                  value={settings.organization.hours}
                  onChange={(e) => handleOrgChange('hours', e.target.value)}
                  placeholder="Lun-Sab 10:00-19:00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Redes Sociales</h3>
            <div className="grid gap-4 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Facebook
                </label>
                <input
                  type="url"
                  value={settings.organization.social.facebook ?? ''}
                  onChange={(e) => handleSocialChange('facebook', e.target.value)}
                  placeholder="https://facebook.com/petmaspe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instagram
                </label>
                <input
                  type="url"
                  value={settings.organization.social.instagram ?? ''}
                  onChange={(e) => handleSocialChange('instagram', e.target.value)}
                  placeholder="https://instagram.com/petmaspe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Twitter
                </label>
                <input
                  type="url"
                  value={settings.organization.social.twitter ?? ''}
                  onChange={(e) => handleSocialChange('twitter', e.target.value)}
                  placeholder="https://twitter.com/petmaspe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  YouTube
                </label>
                <input
                  type="url"
                  value={settings.organization.social.youtube ?? ''}
                  onChange={(e) => handleSocialChange('youtube', e.target.value)}
                  placeholder="https://youtube.com/@petmaspe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
          </div>

          {/* Schema Options */}
          <div className="pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Datos Estructurados (Schema.org)</h3>
            <div className="space-y-4">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={settings.schemaProductEnabled}
                  onChange={(e) => handleChange('schemaProductEnabled', e.target.checked)}
                  className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                />
                <span className="text-gray-700">Schema de Producto (mejora resultados de busqueda)</span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={settings.schemaBreadcrumbEnabled}
                  onChange={(e) => handleChange('schemaBreadcrumbEnabled', e.target.checked)}
                  className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                />
                <span className="text-gray-700">Schema de Breadcrumb (navegacion en busqueda)</span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={settings.schemaOrganizationEnabled}
                  onChange={(e) => handleChange('schemaOrganizationEnabled', e.target.checked)}
                  className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                />
                <span className="text-gray-700">Schema de Organizacion (panel de conocimiento)</span>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
